export type AuthProvider = 'apple' | 'google' | 'microsoft' | 'email';

/** The providers that sign in through an identity provider: every one but email. */
export type SocialProvider = Exclude<AuthProvider, 'email'>;

/** Every provider, in the order every sign-in screen shows them. */
export const DEFAULT_AUTH_PROVIDERS: readonly AuthProvider[] = ['apple', 'google', 'microsoft', 'email'];

const isSocialProvider = (provider: AuthProvider): provider is SocialProvider => provider !== 'email';

/** The social providers, in DEFAULT_AUTH_PROVIDERS' order: what /sign-in and /register offer above the email form. */
export const SOCIAL_AUTH_PROVIDERS: readonly SocialProvider[] = DEFAULT_AUTH_PROVIDERS.filter(isSocialProvider);

/**
 * The social providers the identity app can sign in with: Google (ENG-849) and Microsoft (ENG-924). Apple (ENG-923) is
 * shown but stays disabled until it is added here.
 */
export const IMPLEMENTED_PROVIDERS: readonly SocialProvider[] = ['google', 'microsoft'];

/**
 * The providers to enable from VITE_AUTH_PROVIDERS, a comma-separated list such as "google" that CI sets from
 * infrastructure/identity's `social-providers enabled`: only implemented ones, in SOCIAL_AUTH_PROVIDERS' order. Unknown
 * and unimplemented names are ignored, so a stray value can never enable a button that doesn't work. Callers pass the
 * setting (`import.meta.env.VITE_AUTH_PROVIDERS`); this never reads the environment itself.
 */
export const enabledAuthProviders = (setting: string | undefined): SocialProvider[] => {
  const named = new Set((setting ?? '').split(',').map((name) => name.trim().toLowerCase()));
  return SOCIAL_AUTH_PROVIDERS.filter((provider) => IMPLEMENTED_PROVIDERS.includes(provider) && named.has(provider));
};

/** Whether `provider` is one of `enabled`: false for email, unknown names and disabled providers. */
export const isEnabledProvider = (
  enabled: readonly SocialProvider[],
  provider: string | null | undefined,
): provider is SocialProvider => enabled.includes(provider as SocialProvider);

/**
 * The enabled social providers plus email, which needs no identity provider and is always usable: SocialAuthRow's
 * `enabledProviders` on screens that offer email. `undefined` (every provider enabled) stays `undefined`.
 */
export const withEmail = (enabled: readonly SocialProvider[] | undefined): readonly AuthProvider[] | undefined =>
  enabled === undefined ? undefined : [...enabled, 'email'];
